import {
  dpeActionPrice,
  dpeActionProductCode,
  dpeActionSourceStatus,
  statusKey,
} from './dpeActionPreviewTotals.js';

const normalizedIdentity = (value) => String(value || '').trim().toUpperCase();
const moneyNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const identityValues = (...values) => values
  .flat()
  .map(normalizedIdentity)
  .filter(Boolean);

const evidenceIdentityValues = (source = {}) => identityValues(
  source.referenceId,
  source.referenceID,
  source.ReferenceId,
  source['Reference ID'],
  source.orderItemId,
  source.orderItemID,
  source.OrderItemId,
  source['OrderItem ID'],
  source.id,
  source.Id,
  source.rowKey,
  source.sourceRowKey,
);

const sameMoneyValue = (left, right) => Math.abs(moneyNumber(left) - moneyNumber(right)) < 0.005;
const productCodeForProduct = (product = {}) => (
  product.productCode
  || product.productCd
  || product.oemProductCode
  || product['Product Code']
  || ''
);

const sameProductCodeWhenPresent = (action = {}, product = {}) => {
  const productCode = normalizedIdentity(productCodeForProduct(product));
  const actionCode = normalizedIdentity(dpeActionProductCode(action));
  return !productCode || !actionCode || productCode === actionCode;
};

const sameSourceStatusWhenPresent = (action = {}, product = {}) => {
  const productStatus = product.productStatus || product.status;
  const actionStatus = action.sourceEvidence?.productStatus
    || action.sourceEvidence?.status
    || action.previousProductStatus
    || action.previousStatus;
  return !productStatus || !actionStatus || statusKey(productStatus) === statusKey(actionStatus);
};

const samePriceWhenPresent = (action = {}, product = {}) => {
  const productPrice = product.dealerPrice ?? product.mrr ?? product.price;
  const actionPrice = action.sourceEvidence?.dealerPrice
    ?? action.sourceEvidence?.mrr
    ?? action.sourceEvidence?.price
    ?? action.previousDealerPrice;
  return productPrice === undefined || productPrice === null || actionPrice === undefined || actionPrice === null
    || sameMoneyValue(productPrice, actionPrice);
};

export const dpeActionMatchesProduct = (action = {}, product = {}) => {
  if (!['remove', 'change_price'].includes(action.actionType)) return false;
  const productIds = evidenceIdentityValues(product);
  const actionIds = identityValues(
    evidenceIdentityValues(action.vmtPayload),
    evidenceIdentityValues(action.sourceEvidence),
    evidenceIdentityValues(action.sourceEvidence?.row),
  );
  if (productIds.length && actionIds.length) {
    return productIds.some((id) => actionIds.includes(id))
      && sameProductCodeWhenPresent(action, product)
      && sameSourceStatusWhenPresent(action, product)
      && samePriceWhenPresent(action, product);
  }
  return normalizedIdentity(productCodeForProduct(product)) === normalizedIdentity(dpeActionProductCode(action))
    && statusKey(product.productStatus) === dpeActionSourceStatus(action)
    && sameMoneyValue(product.dealerPrice, dpeActionPrice(action));
};

export const previewForDpeAction = (action = {}) => {
  const label = action.actionType === 'remove'
    ? 'Queued remove from DPE'
    : action.actionType === 'change_price'
      ? 'Queued DPE price change'
      : 'Queued add/update DPE';
  const variant = action.actionType === 'remove'
    ? 'removed'
    : action.actionType === 'change_price'
      ? 'price'
      : 'added';
  return {
    label,
    variant,
    ...(action.actionType === 'change_price' ? { proposedPrice: dpeActionPrice(action) } : {}),
  };
};

export const mergeDpeActionPreviews = ({ products = [], actions = [] } = {}) => {
  const mergedActionKeys = new Set();
  const keyForAction = (action, index) => action?.id || `__index_${index}`;
  const rows = products.map((product) => {
    const actionIndex = actions.findIndex((candidate, index) => (
      !mergedActionKeys.has(keyForAction(candidate, index))
      && dpeActionMatchesProduct(candidate, product)
    ));
    if (actionIndex < 0) return { product, action: null };
    const action = actions[actionIndex];
    mergedActionKeys.add(keyForAction(action, actionIndex));
    return { product, action };
  });
  const standaloneActions = actions.filter((action, index) => !mergedActionKeys.has(keyForAction(action, index)));
  return { rows, standaloneActions };
};
